/**
 * pickerWorkshop — the workshop-picker's own item type, and the mappers from
 * each API shape onto it (BON-146).
 *
 * WHY a view model rather than reusing `api/workshops.ts#Workshop`: the picker
 * now serves two data sources with different envelopes. `Workshop` is flat
 * (`venueName`, `venueRegionName`, `workshopType.treeSpecies`,
 * `localStartDatetime`, `available`) and carries two dozen fields the picker
 * never reads; `RebookTargetRow` nests venue/region/species and names the same
 * facts differently. Casting either to the other would mean fabricating fields,
 * so both are normalized here instead and every downstream helper, row and pill
 * speaks only this shape.
 *
 * ELIGIBILITY IS CARRIED, NOT DERIVED. Each mapper decides `selectable` and
 * `reasonCode` once, at the boundary: the rebook listing is told by the server
 * (which owns the price, capacity, cutoff and source rules), and the
 * create-booking listing derives it from ended/sold-out. Downstream code never
 * asks "which source is this" — it reads `selectable`.
 */
import type { RebookReasonCode, RebookTargetRow } from '../../../api/rebooking';
import type { Workshop } from '../../../api/workshops';

/** Why a row cannot be picked. Shared across both data sources: the
 *  create-booking picker only ever produces `ENDED` / `NO_CAPACITY`, the rebook
 *  listing can produce any of them. */
export type PickerReasonCode = RebookReasonCode;

/**
 * One workshop as the picker sees it. Deliberately small — everything here is
 * rendered somewhere (calendar pill, list row, day drawer, tooltip, footer).
 */
export interface PickerWorkshop {
  id: string;
  /** Absolute UTC instant. Sort/compare only — NEVER displayed (BON-386). */
  startsAt: string;
  /** Venue-local wall clock, naive, no `Z`. The only form displayed. Null only
   *  when the source row's local column is unpopulated. */
  localStartsAt: string | null;
  /** Workshop type display name (`displayName ?? name`). */
  typeName: string;
  /** Drives the tree filter's option key. Null when the type has no species. */
  treeSpeciesId: string | null;
  treeSpeciesName: string | null;
  venueName: string;
  /** Null for a venueless private event (client site or to be determined). */
  venueCity: string | null;
  venueState: string | null;
  /** Region display name, for the day-drawer row. Null when unresolved. */
  regionName: string | null;
  priceCents: number;
  /** Seats remaining. Hold-aware on the rebook listing; confirmed-only on the
   *  create-booking listing until its availability overlay has run. */
  seatsLeft: number;
  selectable: boolean;
  reasonCode: PickerReasonCode | null;
  /** Display copy for `reasonCode`. Null when selectable. */
  reasonLabel: string | null;
}

/** Frontend copy per reason code. The server sends its own `reasonLabel`, but
 *  the picker overrides it so a disabled chip, a list row and the drawer all
 *  read the same words regardless of which layer produced the row. */
const REASON_LABEL: Record<PickerReasonCode, string> = {
  IS_SOURCE: 'Current',
  NO_CAPACITY: 'Sold out',
  PRICE_MISMATCH: 'Different price',
  ENDED: 'Ended',
  NOT_ACTIVE: 'Unavailable',
  REGION_MISMATCH: 'Another region',
};

export function reasonLabelFor(code: PickerReasonCode | null): string | null {
  return code ? REASON_LABEL[code] : null;
}

/** A rebook target refused for capacity may still have seats, just too few for
 *  everyone moving, so it reads "Only N left" rather than "Sold out". */
function rebookReasonLabel(t: RebookTargetRow): string | null {
  if (t.reasonCode === 'NO_CAPACITY' && t.seatsLeft > 0) {
    return `Only ${t.seatsLeft} seat${t.seatsLeft === 1 ? '' : 's'} left`;
  }
  return reasonLabelFor(t.reasonCode) ?? t.reasonLabel;
}

/** The venue-local wall clock (BON-386): the naive `localStartDatetime`, no
 *  `Z`. Falls back to the absolute `startsAt` only when the local column is
 *  unpopulated — a null edge, not the normal path. */
function wallClockOf(w: Workshop): string {
  return w.localStartDatetime ?? w.startsAt;
}

/** A workshop that has already ENDED can't be booked — the API rejects it too
 *  (`assertWorkshopBookable` → 422). Boundary is the END time (`endsAt < now`),
 *  NOT the start: an in-progress workshop stays bookable so a CSR can book a
 *  walk-in; only a finished one is refused. */
function isEndedWorkshop(endsAt: string): boolean {
  const t = new Date(endsAt).getTime();
  return !Number.isNaN(t) && t < Date.now();
}

/**
 * `Workshop` → `PickerWorkshop` for the CREATE-BOOKING picker, which has no
 * server-side eligibility verdict and so derives one. Ended wins over sold out:
 * a finished workshop's seat count is moot, and blaming capacity would send the
 * CSR looking for a later date at the same venue.
 */
export function fromWorkshop(w: Workshop): PickerWorkshop {
  const ended = isEndedWorkshop(w.endsAt);
  const soldOut = w.available <= 0;
  const reasonCode: PickerReasonCode | null = ended ? 'ENDED' : soldOut ? 'NO_CAPACITY' : null;
  return {
    id: w.id,
    startsAt: w.startsAt,
    localStartsAt: wallClockOf(w),
    typeName: w.workshopType.displayName ?? w.workshopType.name,
    treeSpeciesId: w.workshopType.treeSpecies?.id ?? null,
    treeSpeciesName: w.workshopType.treeSpecies?.name ?? null,
    venueName: w.venueName,
    venueCity: w.venueCity,
    venueState: w.venueState,
    regionName: w.venueRegionName,
    priceCents: w.priceCents,
    seatsLeft: w.available,
    selectable: reasonCode === null,
    reasonCode,
    reasonLabel: reasonLabelFor(reasonCode),
  };
}

/**
 * `RebookTargetRow` → `PickerWorkshop`. The server has already ruled on
 * selectability against rules the client cannot see (same price as what this
 * booking paid, hold-aware capacity for THIS booking's seat count, which
 * workshop is the source), so this mapper never second-guesses it.
 *
 * The `'Workshop'` last resort is unreachable today — `workshopType.name` is
 * non-null on both sides of the wire — and is kept only because a blank chip is
 * a worse failure than a generic word.
 */
export function fromRebookTarget(t: RebookTargetRow): PickerWorkshop {
  return {
    id: t.id,
    startsAt: t.startsAt,
    localStartsAt: t.localStartsAt,
    typeName: t.workshopType.displayName ?? t.workshopType.name ?? 'Workshop',
    treeSpeciesId: t.treeSpecies?.id ?? null,
    treeSpeciesName: t.treeSpecies?.name ?? null,
    venueName: t.venue.name,
    venueCity: t.venue.city,
    venueState: t.venue.state,
    regionName: t.region?.name ?? null,
    priceCents: t.priceCents,
    seatsLeft: t.seatsLeft,
    selectable: t.selectable,
    reasonCode: t.reasonCode,
    // Frontend copy wins over the server's, so the chip and the drawer agree.
    // Falls back to the server label for any code this build does not know —
    // a new code then reads as itself rather than as nothing.
    reasonLabel: t.selectable ? null : rebookReasonLabel(t),
  };
}
